import type {
  BalanceRegistration,
  BalanceSlot,
} from '@sportninja/common/services/teamBalance'
import { dollarsToMinor } from './balanceMath'

/**
 * Tax on top — the sheet's cash figures, read from the server's balance ledger.
 *
 * A Player Share is PRE-TAX: invoices add tax to it, so the cash a team collects
 * is more than its shares. Every progress denominator therefore has to be the
 * server's cash INCLUDING tax (`expected_total`, `threshold_total`), never the
 * share or the team cost. Nothing here multiplies by a rate: tax is only ever
 * read from the server, which also knows about invoices issued before this rule
 * (their tax was inside the share, and they keep it).
 *
 * Each figure falls back to the pre-tax number when an older server does not send
 * the cash one yet, which is exactly what those servers invoiced.
 */

/** What this slot is expected to bring in, tax included (minor units). */
export const slotExpectedMinor = (
  slot: Pick<BalanceSlot, 'amount' | 'expected_total'>
): number => dollarsToMinor(slot.expected_total ?? slot.amount)

/**
 * The PLAYER SHARE cell's tax line.
 *
 *  - `on_top`   the share plus its tax is what the player pays (new invoices, and
 *               any slot not invoiced yet): "+ $7.50 tax".
 *  - `included` an invoice issued before tax on top: the share already held its
 *               tax, so "+" would be false: "incl. $7.14 tax".
 *  - `invoiced` the invoices no longer match the share at all (an admin edited
 *               one): state their total rather than a relationship that is not
 *               there.
 *
 * Null when there is no tax to mention, or the server does not send the figures.
 */
export type ShareTaxNote =
  | { kind: 'on_top'; taxMinor: number }
  | { kind: 'included'; taxMinor: number }
  | { kind: 'invoiced'; totalMinor: number }

export const shareTaxNote = (
  slot: Pick<BalanceSlot, 'amount' | 'expected_total' | 'expected_tax'>
): ShareTaxNote | null => {
  if (
    slot.expected_total === undefined ||
    slot.expected_total === null ||
    slot.expected_tax === undefined ||
    slot.expected_tax === null
  ) {
    return null
  }

  const taxMinor = dollarsToMinor(slot.expected_tax)
  if (taxMinor <= 0) {
    return null
  }

  const totalMinor = dollarsToMinor(slot.expected_total)
  const shareMinor = dollarsToMinor(slot.amount)

  if (totalMinor - taxMinor === shareMinor) {
    return { kind: 'on_top', taxMinor }
  }
  if (totalMinor === shareMinor) {
    return { kind: 'included', taxMinor }
  }

  return { kind: 'invoiced', totalMinor }
}

/**
 * The collection figures' denominator across the registrations on screen, in
 * minor units: what the teams owe in cash including tax.
 *
 * `tax` is the part of `amountDue` that is tax, and null unless EVERY registration
 * reports it — a partial sum would understate it.
 */
export const amountDueTotals = (
  registrations: readonly Pick<BalanceRegistration, 'team_cost' | 'kpis'>[]
): { amountDue: number; tax: number | null } =>
  registrations.reduce<{ amountDue: number; tax: number | null }>(
    (totals, registration) => {
      const expectedTotal = registration.kpis?.expected_total
      const expectedTax = registration.kpis?.expected_tax

      return {
        amountDue:
          totals.amountDue +
          dollarsToMinor(Number(expectedTotal ?? registration.team_cost) || 0),
        tax:
          totals.tax === null ||
          expectedTax === undefined ||
          expectedTax === null
            ? null
            : totals.tax + dollarsToMinor(Number(expectedTax) || 0),
      }
    },
    { amountDue: 0, tax: 0 }
  )

/**
 * The qualifying minimum in cash including tax (minor units), or null when the
 * division sets none. Same fallback rule: the pre-tax threshold from an older
 * server.
 */
export const qualifyingMinimumMinor = (
  registration: Pick<BalanceRegistration, 'threshold' | 'kpis'>
): number | null => {
  const thresholdTotal = registration.kpis?.threshold_total
  const minimum =
    thresholdTotal === undefined ? registration.threshold : thresholdTotal

  return minimum === null || minimum === undefined
    ? null
    : dollarsToMinor(Number(minimum))
}

/**
 * SN-8310 — the Team Budget header's money, in minor units, from the server's
 * balance ledger (`kpis`) for the registrations on screen.
 *
 * NOT from the sheet's rows. The rows are what the Show filter and Display
 * Cancelled leave on screen, so a header summed over them changed its verdict
 * with a filter ("Show: Open slots" turned "$300 more to qualify" into "$500"),
 * vanished when a filter matched nothing, and never counted money paid by a
 * player who has since been released. The grid's total row still follows the
 * filters; the header answers what the TEAM owes, whatever is on screen.
 *
 *  - `paid`         verified cash in, refund-net, released players included.
 *  - `amountDue`    cash owed in all, tax included (`amountDueTotals`).
 *  - `outstanding`  `amountDue − paid`, the server's own figure; NEGATIVE is a
 *                   credit.
 *  - `minimum`      the qualifying minimum in cash, for ONE registration only —
 *                   minimums across divisions are not one target. Null when
 *                   the division sets none.
 *  - `tax`          the part of `amountDue` that is tax, or null unless every
 *                   registration reports it.
 */
export interface HeaderMoney {
  paid: number
  amountDue: number
  outstanding: number
  minimum: number | null
  tax: number | null
}

export const headerMoney = (
  registrations: readonly Pick<
    BalanceRegistration,
    'team_cost' | 'threshold' | 'kpis'
  >[]
): HeaderMoney => {
  const { amountDue, tax } = amountDueTotals(registrations)
  const sum = (pick: (kpis: BalanceRegistration['kpis']) => number) =>
    registrations.reduce(
      (total, registration) =>
        total + dollarsToMinor(Number(pick(registration.kpis)) || 0),
      0
    )

  return {
    paid: sum((kpis) => kpis?.total_paid),
    amountDue,
    tax,
    outstanding: sum((kpis) => kpis?.outstanding),
    minimum:
      registrations.length === 1
        ? qualifyingMinimumMinor(registrations[0])
        : null,
  }
}

/**
 * The Record Offline Payment amount to start from: what the player still owes,
 * tax included. Prefilled so a rep recording "the rest" does not type the share
 * and leave its tax behind as a balance. Null when nothing is owed.
 */
export const offlinePaymentPrefill = (
  slot: Pick<BalanceSlot, 'owed'> | null
): number | null => {
  const owed = Number(slot?.owed)

  return Number.isFinite(owed) && owed > 0 ? owed : null
}
